import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { web } from "@/config/System";
import { getLinimasa, getTimeServer } from "@/services/getData";
import {
  Award,
  CalendarDays,
  CheckCircle2,
  Download,
  FileText,
  LogIn,
  Megaphone,
  ScrollText,
  Sparkles,
  Trophy,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const formatDateToIndonesian = (date?: string | null) => {
  if (!date) return "-";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";

  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
};

const parseDateTime = (date?: string | null) => {
  if (!date) return null;

  const parsed = new Date(date.replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getPeriodeBySlug = (
  linimasa: Record<string, any[]> | undefined,
  slug: string,
) => {
  if (!linimasa) return null;

  const normalizedSlug = slug.toLowerCase();

  for (const items of Object.values(linimasa)) {
    const periode = items.find(
      (item) =>
        item.jalur_jenis?.nama_jalur_jenis_slug?.toLowerCase() ===
        normalizedSlug,
    );

    if (periode) return periode;
  }

  return null;
};

const getPeriodeNonCbt = (linimasa: Record<string, any[]> | undefined) => {
  const possibleSlugs = [
    "prestasi",
    "talentscouting",
    "kepemimpinan",
    "skorutbk",
    "sekolahlab",
  ];

  if (!linimasa) return null;

  for (const slug of possibleSlugs) {
    const periode = getPeriodeBySlug(linimasa, slug);
    if (periode) return periode;
  }

  return null;
};

export default function Announcement() {
  // const [_, setActiveTab] = useState("nonCbt");
  const [activeTab, setActiveTab] = useState("");
  const { data: linimasaResponse } = useQuery({
    queryKey: ["linimasa"],
    queryFn: getLinimasa,
  });

  const { data: timeServerResponse } = useQuery({
    queryKey: ["time-server"],
    queryFn: getTimeServer,
  });

  const linimasa = linimasaResponse?.data ?? linimasaResponse;

  const timeServer = timeServerResponse?.data ?? timeServerResponse;
  const waktuCurrentServer = parseDateTime(timeServer);

  const periodeNonCbt = useMemo(() => getPeriodeNonCbt(linimasa), [linimasa]);
  const periodeCbt = useMemo(
    () => getPeriodeBySlug(linimasa, "cbt"),
    [linimasa],
  );
  const periodeSnbt = useMemo(
    () => getPeriodeBySlug(linimasa, "snbt"),
    [linimasa],
  );
  const periodeSnbp = useMemo(
    () => getPeriodeBySlug(linimasa, "snbp"),
    [linimasa],
  );

  const isPengumumanLulusVisible = (
    waktuPengumumanKelulusan?: string | null,
  ) => {
    const waktuPengumuman = parseDateTime(waktuPengumumanKelulusan);

    if (!waktuPengumuman || !waktuCurrentServer) return false;

    return waktuCurrentServer.getTime() >= waktuPengumuman.getTime();
  };

  // const docLink = periodeSnbp?.url_panduan_daftar_kembali;
  // if (!docLink) {
  //   return null; // atau sembunyikan tombolnya
  // }

  // console.log("periodeNonCbt", periodeNonCbt);
  // console.log("periodeCbt", periodeCbt);
  // console.log("periodeSnbt", periodeSnbt);
  // console.log("periodeSnbp", periodeSnbp);

  // console.log(
  //     "nonCbt waktu_pengumuman_kelulusan",
  //     periodeNonCbt?.waktu_pengumuman_kelulusan
  // );

  console.log("waktuCurrentServer", waktuCurrentServer);

  const dataPengumuman = {
    nonCbt: {
      id: "non-cbt",
      active: isPengumumanLulusVisible(
        periodeNonCbt?.waktu_pengumuman_kelulusan,
      ),
      theme: "indigo",
      title: "Jalur Mandiri (Non-CBT)",
      heroTitle:
        "Selamat! kepada peserta yang telah Lulus Jalur Mandiri (Non-CBT).",
      desc: "Seleksi mandiri non-CBT melalui jalur prestasi, raport, kepemimpinan, talentscouting, atau kemitraan.",
      chips: [
        "Skor UTBK",
        "Raport",
        "Talent scouting",
        "Kepemimpinan",
        "Sekolah Lab",
      ],
      dates: periodeNonCbt
        ? {
            startUkt: formatDateToIndonesian(periodeNonCbt.waktu_mulai_isi_ukt),
            endUkt: formatDateToIndonesian(periodeNonCbt.waktu_selesai_isi_ukt),
            startPengumuman: formatDateToIndonesian(
              periodeNonCbt.waktu_pengumuman,
            ),
            startPembayaran: formatDateToIndonesian(
              periodeNonCbt.waktu_mulai_bayar_ukt,
            ),
            endPembayaran: formatDateToIndonesian(
              periodeNonCbt.waktu_selesai_bayar_ukt,
            ),
          }
        : {
            startUkt: "-",
            endUkt: "-",
            startPengumuman: "-",
            startPembayaran: "-",
            endPembayaran: "-",
          },
      //docLink: "/panduan/Panduan-Pendaftaran-Kembali-Jalur-Non-CBT.pdf",
      docLink: periodeNonCbt?.url_panduan_daftar_kembali,
      icon: <ScrollText className="w-4 h-4 mr-2" />,
    },
    cbt: {
      id: "cbt",
      active: isPengumumanLulusVisible(periodeCbt?.waktu_pengumuman_kelulusan),
      theme: "green",
      title: "Jalur Mandiri (SMBJM-CBT)",
      heroTitle:
        "Selamat! kepada peserta yang telah Lulus Jalur Mandiri (SMBJM-CBT).",
      desc: "Seleksi mandiri melalui ujian komputer (CBT), penelusuran nilai raport, kepemimpinan, talentscouting, dan kemitraan sekolah lab.",
      chips: ["CBT"],
      dates: periodeCbt
        ? {
            startUkt: formatDateToIndonesian(periodeCbt.waktu_mulai_isi_ukt),
            endUkt: formatDateToIndonesian(periodeCbt.waktu_selesai_isi_ukt),
            startPengumuman: formatDateToIndonesian(
              periodeCbt.waktu_pengumuman,
            ),
            startPembayaran: formatDateToIndonesian(
              periodeCbt.waktu_mulai_bayar_ukt,
            ),
            endPembayaran: formatDateToIndonesian(
              periodeCbt.waktu_selesai_bayar_ukt,
            ),
          }
        : {
            startUkt: "-",
            endUkt: "-",
            startPengumuman: "-",
            startPembayaran: "-",
            endPembayaran: "-",
          },
      //docLink: "/panduan/Panduan-Pendaftaran-Kembali-Jalur Mandiri-CBT_Signed.pdf",
      docLink: periodeCbt?.url_panduan_daftar_kembali,
      icon: <Award className="w-4 h-4 mr-2" />,
    },
    snbt: {
      id: "nasional",
      active: isPengumumanLulusVisible(periodeSnbt?.waktu_pengumuman_kelulusan),
      theme: "blue",
      title: "Jalur Nasional (SNBT)",
      heroTitle: "Selamat! kepada peserta yang telah Lulus Jalur SNBT.",
      desc: "Seleksi Nasional Berbasis Prestasi",
      chips: ["SNBT"],
      dates: periodeSnbt
        ? {
            startUkt: formatDateToIndonesian(periodeSnbt.waktu_mulai_isi_ukt),
            endUkt: formatDateToIndonesian(periodeSnbt.waktu_selesai_isi_ukt),
            startPengumuman: formatDateToIndonesian(
              periodeSnbt.waktu_pengumuman,
            ),
            startPembayaran: formatDateToIndonesian(
              periodeSnbt.waktu_mulai_bayar_ukt,
            ),
            endPembayaran: formatDateToIndonesian(
              periodeSnbt.waktu_selesai_bayar_ukt,
            ),
          }
        : {
            startUkt: "-",
            endUkt: "-",
            startPengumuman: "-",
            startPembayaran: "-",
            endPembayaran: "-",
          },
      //docLink: "/panduan/Panduan-Pendaftaran-Kembali-SNBT-2026rv1.pdf",
      docLink: periodeSnbt?.url_panduan_daftar_kembali,
      icon: <ScrollText className="w-4 h-4 mr-2" />,
    },
    snbp: {
      id: "nasional",
      active: isPengumumanLulusVisible(periodeSnbp?.waktu_pengumuman_kelulusan),
      theme: "blue",
      title: "Jalur Nasional (SNBP)",
      heroTitle: "Selamat! kepada peserta yang telah Lulus Jalur SNBP.",
      desc: "Seleksi Nasional Berbasis Prestasi",
      chips: ["SNBP"],
      dates: periodeSnbp
        ? {
            startUkt: formatDateToIndonesian(periodeSnbp.waktu_mulai_isi_ukt),
            endUkt: formatDateToIndonesian(periodeSnbp.waktu_selesai_isi_ukt),
            startPengumuman: formatDateToIndonesian(
              periodeSnbp.waktu_pengumuman,
            ),
            startPembayaran: formatDateToIndonesian(
              periodeSnbp.waktu_mulai_bayar_ukt,
            ),
            endPembayaran: formatDateToIndonesian(
              periodeSnbp.waktu_selesai_bayar_ukt,
            ),
          }
        : {
            startUkt: "-",
            endUkt: "-",
            startPengumuman: "-",
            startPembayaran: "-",
            endPembayaran: "-",
          },
      docLink: periodeSnbp?.url_panduan_daftar_kembali,
      icon: <ScrollText className="w-4 h-4 mr-2" />,
    },
  };

  const themeActiveClasses: Record<string, string> = {
    blue: "data-[state=active]:bg-blue-50 data-[state=active]:border-blue-200 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900/30 dark:data-[state=active]:border-blue-800 dark:data-[state=active]:text-blue-400",
    green:
      "data-[state=active]:bg-emerald-50 data-[state=active]:border-emerald-200 data-[state=active]:text-emerald-700 dark:data-[state=active]:bg-emerald-900/30 dark:data-[state=active]:border-emerald-800 dark:data-[state=active]:text-emerald-400",
    indigo:
      "data-[state=active]:bg-indigo-50 data-[state=active]:border-indigo-200 data-[state=active]:text-indigo-700 dark:data-[state=active]:bg-indigo-900/30 dark:data-[state=active]:border-indigo-800 dark:data-[state=active]:text-indigo-400",
  };

  // const activeTabs = Object.entries(dataPengumuman).filter(
  //   ([_, data]) => data.active,
  // );

  const activeTabs = Object.entries(dataPengumuman).filter(
    ([_, data]) => data.active,
  );

  //const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    if (activeTabs.length === 0) return;

    const hasCurrentTab = activeTabs.some(([key]) => key === activeTab);

    if (!activeTab || !hasCurrentTab) {
      setActiveTab(activeTabs[0][0]);
    }
  }, [activeTabs, activeTab]);
  const isMultipleTabs = activeTabs.length > 1;

  return (
    <div className="container mx-auto lg:px-20 py-8 px-4 transition-colors duration-300">
      <div className="flex items-center gap-10 mb-5">
        <img
          src={web.maskot}
          alt="maskot-spmb-undiksha"
          width={150}
          className="md:block hidden"
        />
        <div className="text-start space-y-3 flex-1">
          <Badge
            variant="outline"
            className="border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400 px-3 py-1"
          >
            <Megaphone className="w-3 h-3 mr-2" />
            Pengumuman Penerimaan {web.univAcronym}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Hasil Seleksi Penerimaan Mahasiswa Baru
          </h1>
          <div className="relative">
            <p className="md:w-auto w-[80%] text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {isMultipleTabs
                ? "Silakan pilih tab jalur di bawah ini untuk melihat detail linimasa dan panduan pendaftaran kembali masing-masing jalur."
                : "Berikut adalah detail jadwal pendaftaran kembali beserta dokumen panduan untuk jalur kelulusan Anda."}
            </p>
            <img
              src={web.maskot}
              alt="maskot-spmb-undiksha"
              width={70}
              className="md:opacity-0 absolute -top-5 right-0 z-10"
            />
          </div>
        </div>
      </div>

      {/* <Tabs defaultValue="nonCbt" className="w-full" onValueChange={setActiveTab}> */}
      <Tabs value={activeTab} className="w-full" onValueChange={setActiveTab}>
        <div className="w-full relative mb-5">
          <TabsList
            className="flex w-full items-center justify-start sm:justify-center gap-2 overflow-x-auto snap-x snap-mandatory bg-transparent p-1 px-4 sm:px-1 h-auto
            [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {Object.entries(dataPengumuman)
              .filter(([_, data]) => data.active)
              .map(([key, data]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className={`snap-start shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-all shadow-sm border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 ${themeActiveClasses[data.theme] || themeActiveClasses.blue}`}
                >
                  <div className="flex items-center justify-center gap-2">
                    {data.icon}
                    {data.title}
                  </div>
                </TabsTrigger>
              ))}
          </TabsList>
        </div>

        {Object.entries(dataPengumuman)
          .filter(([_, data]) => data.active)
          .map(([key, data]) => (
            <TabsContent
              key={key}
              value={key}
              className="mt-0 animate-in fade-in slide-in-from-bottom-4 duration-500"
            >
              <AnnouncementContent data={data} />
            </TabsContent>
          ))}
      </Tabs>
    </div>
  );
}

function AnnouncementContent({ data }: { data: any }) {
  const slug = data?.id;
  const isNasional = slug === "nasional";
  const isCbt = slug === "cbt";
  const isNonCbt = slug === "non-cbt";

  const styles = {
    heroGradient:
      data.theme === "green"
        ? "from-emerald-600 to-teal-700 dark:from-emerald-900 dark:to-teal-950"
        : data.theme === "blue"
          ? "from-indigo-600 to-blue-700 dark:from-indigo-900 dark:to-blue-950"
          : "from-violet-600 to-indigo-700 dark:from-violet-900 dark:to-indigo-950",
    textLight:
      data.theme === "green"
        ? "text-emerald-100"
        : data.theme === "blue"
          ? "text-blue-100"
          : "text-indigo-100",
    timelineColor:
      data.theme === "green"
        ? "bg-emerald-600 dark:bg-emerald-500"
        : data.theme === "blue"
          ? "bg-blue-600 dark:bg-blue-500"
          : "bg-indigo-600 dark:bg-indigo-500",
    timelineBorder:
      data.theme === "green"
        ? "border-emerald-100 dark:border-slate-800"
        : data.theme === "blue"
          ? "border-blue-100 dark:border-slate-800"
          : "border-indigo-100 dark:border-slate-800",
    btnPrimary:
      data.theme === "green"
        ? "bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500 shadow-emerald-200 dark:shadow-none"
        : data.theme === "blue"
          ? "bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 shadow-blue-200 dark:shadow-none"
          : "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500 shadow-indigo-200 dark:shadow-none",
    cardBg:
      data.theme === "green"
        ? "bg-emerald-50/30 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-900"
        : data.theme === "blue"
          ? "bg-blue-50/30 border-blue-200 dark:bg-blue-950/20 dark:border-blue-900"
          : "bg-indigo-50/30 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-900",
    iconColor:
      data.theme === "green"
        ? "text-emerald-600 dark:text-emerald-400"
        : data.theme === "blue"
          ? "text-blue-600 dark:text-blue-400"
          : "text-indigo-600 dark:text-indigo-400",
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <Card
        className={`md:col-span-8 overflow-hidden relative border-0 shadow-lg bg-gradient-to-br ${styles.heroGradient} text-white transition-colors duration-300`}
      >
        <div className="absolute top-0 right-0 p-12 opacity-10">
          <Trophy className="w-64 h-64 -mr-10 -mt-10 rotate-12" />
        </div>

        <CardHeader className="relative z-10">
          <div className={`flex items-center gap-2 ${styles.textLight} mb-2`}>
            <Sparkles className="w-5 h-5 text-yellow-300" />
            <span className="font-medium tracking-wide uppercase text-xs">
              Status Kelulusan: {data.id.toUpperCase()}
            </span>
          </div>
          <CardTitle className="text-3xl md:text-4xl font-bold leading-tight text-white">
            {data.heroTitle}
          </CardTitle>
          <CardDescription
            className={`${styles.textLight}/90 text-lg mt-2 flex flex-col gap-2`}
          >
            {isNasional ? (
              <span className="text-white font-medium mt-1">
                Peserta Lulus {data.title} tidak perlu registrasi akun baru.
                Silakan reset/minta password terlebih dahulu menggunakan email
                SNPMB pusat, kemudian login untuk melanjutkan proses daftar
                kembali.
              </span>
            ) : isCbt ? (
              <span className="text-white font-medium mt-1">
                Silakan login menggunakan akun pendaftaran untuk melihat hasil
                seleksi pada card jalur CBT.
              </span>
            ) : isNonCbt ? (
              <span className="text-white font-medium mt-1">
                Silakan login menggunakan akun pendaftaran untuk melihat hasil
                seleksi pada card jalur yang Anda ikuti.
              </span>
            ) : (
              <span className="text-white font-medium mt-1">
                Silakan login menggunakan akun pendaftaran untuk melihat hasil
                seleksi.
              </span>
            )}
            <div className="flex flex-col sm:flex-row gap-3 mt-3">
              {isNasional && (
                <Link to={"/forgot-password"}>
                  <Button className="bg-white text-slate-900 hover:bg-slate-100 font-semibold shadow-sm w-full sm:w-auto">
                    <LogIn className="w-4 h-4 mr-2" />
                    Minta Password
                  </Button>
                </Link>
              )}
              <Link to={"/login"}>
                <Button className="bg-white/10 border border-white/40 text-white hover:bg-white/20 font-semibold shadow-sm w-full sm:w-auto backdrop-blur-md">
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </Button>
              </Link>
            </div>
          </CardDescription>
        </CardHeader>

        <CardFooter className="relative z-10 pt-8 pb-8">
          <div className="flex flex-wrap gap-3">
            {data.chips.map((chip: string, idx: number) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-white/10 dark:bg-black/30 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20 text-sm text-white"
              >
                <CheckCircle2 className="w-4 h-4 text-green-400" />
                <span>{chip}</span>
              </div>
            ))}
          </div>
        </CardFooter>
      </Card>

      <Card className="md:col-span-4 border-slate-200 dark:border-slate-800 shadow-sm flex flex-col h-full bg-white dark:bg-slate-900 transition-colors duration-300">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg dark:text-white">
            <CalendarDays className={`w-5 h-5 ${styles.iconColor}`} />
            Linimasa
          </CardTitle>
          <CardDescription className="dark:text-slate-400">
            Jadwal pendaftaran kembali jalur ini
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1">
          <div
            className={`relative pl-4 border-l-2 ${styles.timelineBorder} space-y-6 my-2`}
          >
            <div className="relative">
              <span
                className={`absolute -left-[21px] top-1 w-3 h-3 ${styles.timelineColor} rounded-full ring-4 ring-white dark:ring-slate-900`}
              />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-0.5">
                Mulai Isi UKT
              </p>
              <p className="text-base font-bold text-slate-900 dark:text-white">
                {data.dates.startUkt} - {data.dates.endUkt}
              </p>
              <Badge
                variant="secondary"
                className="mt-2 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                Wajib Tepat Waktu
              </Badge>
            </div>
            <div className="relative">
              <span className="absolute -left-[21px] top-1 w-3 h-3 bg-gray-400 dark:bg-slate-600 rounded-full ring-4 ring-white dark:ring-slate-900" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-0.5">
                Pengumuman UKT
              </p>
              <p className="text-base font-bold text-slate-900 dark:text-white">
                {data.dates.startPengumuman}
              </p>
            </div>
            <div className="relative">
              <span className="absolute -left-[21px] top-1 w-3 h-3 bg-gray-400 dark:bg-slate-600 rounded-full ring-4 ring-white dark:ring-slate-900" />
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-0.5">
                Pembayaran UKT / Cetak NIM
              </p>
              <p className="text-base font-bold text-slate-900 dark:text-white">
                {data.dates.startPembayaran} - {data.dates.endPembayaran}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="md:col-span-4 border-slate-200 dark:border-slate-800 shadow-sm bg-slate-50/50 dark:bg-slate-900/50">
        <CardHeader>
          <CardTitle className="text-base font-semibold flex items-center gap-2 dark:text-white">
            <FileText className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            Dokumen Panduan
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
            Unduh panduan pendaftaran kembali untuk {data.title}.
          </p>
          <Button
            variant="outline"
            className="w-full bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-700 justify-between group"
            asChild
          >
            <a href={data.docLink} target="_blank">
              <span>Unduh PDF</span>
              <Download className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-200 transition-colors" />
            </a>
          </Button>
        </CardContent>
      </Card>

      <Card
        className={`md:col-span-8 shadow-sm ${styles.cardBg} transition-colors duration-300`}
      >
        <div className="flex flex-col md:flex-row items-center justify-between p-6 gap-6">
          <div className="space-y-2 flex-1">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              Siap mendaftar ulang?
              <Badge className="bg-green-600 hover:bg-green-700 text-white border-none">
                Dibuka
              </Badge>
            </h3>

            <div className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
              {isNasional ? (
                <p>
                  Bagi peserta yang lulus melalui {data.title},{" "}
                  <span className="font-bold italic">
                    tidak perlu melakukan Register.
                  </span>{" "}
                  Silakan login menggunakan{" "}
                  <span className="font-bold italic">email SNPMB</span> pusat
                  dan lakukan permintaan{" "}
                  <span className="font-bold italic">
                    pengaturan ulang password.
                  </span>
                </p>
              ) : isCbt ? (
                <p>
                  Silakan login ke sistem menggunakan akun yang telah Anda
                  gunakan saat mendaftar pada {data.title}.
                </p>
              ) : (
                <p>
                  Silakan login ke sistem menggunakan akun yang telah Anda
                  gunakan saat mendaftar pada {data.title}.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            {isNasional && (
              <Link to={"/forgot-password"}>
                <Button
                  size="lg"
                  className={`md:w-auto w-full text-white shadow-md transition-all duration-200 border-none ${styles.btnPrimary}`}
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Minta Password
                </Button>
              </Link>
            )}

            <Link to={"/login"}>
              <Button
                variant={"outline"}
                size="lg"
                className="w-full sm:w-auto"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}
